<template>
  <modal
    :show="show"
    modal-classes="modal-lg"
    @update:show="$emit('update:show', $event)"
  >
    <template v-slot:header>
      <h5 class="modal-title">
        <i class="fas fa-receipt mr-2"></i>Tip Details
      </h5>
    </template>

    <div v-if="charge">
      <!-- Friendly Summary -->
      <div class="mb-3">
        <p class="lead mb-1" v-if="!charge.unmatched">
          A tip of
          <strong class="text-success">{{ charge.tipAmountFormatted }}</strong>
          at <strong>{{ charge.locationName }}</strong> is being split among
          <strong>{{ charge.recipientCount }} recipient(s)</strong>.
        </p>
        <p class="lead mb-1" v-else>
          A tip of
          <strong class="text-warning">{{ charge.tipAmountFormatted }}</strong>
          at <strong>{{ charge.locationName }}</strong> could not be matched to
          any employees.
        </p>
        <p class="text-muted text-sm mb-0">
          <i class="far fa-clock mr-1"></i>{{ charge.tipDateFormatted }}
          <span class="mx-2">•</span>
          <copy-text :text="charge.tipId" label="Tip ID" inline>
            Tip ID: {{ truncateId(charge.tipId) }}
          </copy-text>
          <template v-if="charge.code">
            <span class="mx-2">•</span>
            Code: {{ charge.code }}
          </template>
        </p>
      </div>

      <!-- Recipients Table -->
      <div v-if="charge.recipients?.length > 0">
        <div class="table-responsive">
          <table class="table table-sm">
            <thead class="thead-light">
              <tr>
                <th>Employee</th>
                <th>Staff Id</th>
                <th>Share Amount</th>
                <th>Reason</th>
                <th>Shift</th>
                <th>Job</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(recipient, idx) in charge.recipients" :key="idx">
                <td>
                  <strong>{{ recipient.name }}</strong>
                  <br />
                  <small class="text-muted">{{ recipient.email }}</small>
                </td>
                <td>
                  <span class="text-monospace">{{ recipient.staffId }}</span>
                </td>
                <td>
                  <strong class="text-success">{{
                    recipient.shareAmountFormatted
                  }}</strong>
                  <span
                    v-if="
                      charge.recipientCount > 1 &&
                      !isRoundingReason(recipient.reason)
                    "
                    class="badge badge-light ml-1"
                    >{{
                      getSharePercentage(
                        recipient.shareAmount,
                        charge.tipAmount
                      )
                    }}</span
                  >
                </td>
                <td>
                  <reason-badge :reason="recipient.reason" />
                </td>
                <td>
                  <small
                    >{{ recipient.shiftStartFormatted }}<br />{{
                      recipient.shiftEndFormatted
                    }}</small
                  >
                </td>
                <td>
                  <span class="badge badge-primary">{{ recipient.job }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Unmatched Message -->
      <div v-else class="alert alert-warning">
        <i class="fas fa-exclamation-triangle mr-2"></i>
        <strong>{{ charge.unmatchedReason }}</strong>
        <p class="mb-0 mt-2">
          This tip could not be matched to any employee timesheet for this
          location during the time the tip was received.
        </p>
      </div>
    </div>

    <template v-slot:footer>
      <button
        type="button"
        class="btn btn-secondary"
        @click="$emit('update:show', false)"
      >
        Close
      </button>
    </template>
  </modal>
</template>

<script>
import Modal from "@/components/Modal";
import CopyText from "@/components/CopyText";
import ReasonBadge from "@/components/ReasonBadge";
import { truncateId } from "@/utils/formatters";

export default {
  name: "TipDetailsModal",
  components: {
    Modal,
    CopyText,
    ReasonBadge,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    charge: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:show"],
  methods: {
    truncateId,
    isRoundingReason(reason) {
      const r = reason?.toLowerCase() || "";
      return (
        r.includes("remainder") || r.includes("rounding") || r === "remainder"
      );
    },
    getSharePercentage(shareAmount, tipAmount) {
      if (!tipAmount) return "0%";
      const percentage = ((shareAmount / tipAmount) * 100).toFixed(1);
      return `${percentage}%`;
    },
  },
};
</script>

<style lang="scss" scoped>
.text-monospace {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
    "Courier New", monospace;
  font-size: 0.8rem;
}

:deep(.modal-body) {
  padding-top: 0 !important;
}

:deep(.modal-header) {
  padding-bottom: 0.5rem;
}

:deep(.modal-body .lead) {
  margin-top: 0 !important;
}
</style>
